import { FunctionComponent, SyntheticEvent, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import {
	listSelector,
	removeTrackerSignalResultFromInbox,
} from "@/pages/Private/redux/list/list.slice";
import { useIgnoreTrackerSignalMutation } from "@/pages/Private/redux/signal/signal.api";
import { CopywritingStatus } from "@/pages/Private/pages/Messages/components/GenerateBar/types";
import { isDummyProspect } from "@/pages/Private/pages/List/utils/columns/dummyProspect";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { SignalPreview } from "./signalTypes";
import {
	SignalIgnoreFeedbackFormValues,
	SignalIgnoreFeedbackModal,
} from "./SignalIgnoreFeedbackModal";

export interface SignalActionContext {
	listPersonId?: number;
	prospectId?: number;
	trackerOrganizationId?: number | null;
}

interface SignalActionBarProps {
	signal: SignalPreview;
	prospectName: string;
	context?: SignalActionContext;
	onReachOut?: () => void;
	onGenerateDrafts?: () => void;
}

export const SignalActionBar: FunctionComponent<SignalActionBarProps> = ({
	signal,
	prospectName,
	context,
	onReachOut,
	onGenerateDrafts,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const isCopywritingActive = useIsFeatureActive(FeaturesCodes.COPYWRITING_SETTINGS);
	// The lead's persisted copywriting status (QUEUED/RUNNING) drives the generate
	// button's loading state directly — same source the Lead "ongoing" tag uses.
	const listPerson = useAppSelector(listSelector).inbox.find((p) => p.id === context?.listPersonId);
	const copywritingStatus = listPerson?.copywritingStatus;
	const isGeneratingDrafts =
		copywritingStatus === CopywritingStatus.QUEUED ||
		copywritingStatus === CopywritingStatus.RUNNING;
	// Placeholder ("SELECT" / "Person not found") rows have no real contact to
	// write to — block draft generation just like the chat tab's GenerateBar.
	const prospectNotResolved = listPerson ? isDummyProspect(listPerson) : false;
	const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [ignoreTrackerSignal, { isLoading }] = useIgnoreTrackerSignalMutation();

	const stopPropagation = (event: SyntheticEvent<HTMLElement>) => {
		event.stopPropagation();
	};

	const handleIgnoreSubmit = async ({ feedback }: SignalIgnoreFeedbackFormValues) => {
		setSubmitError(null);

		try {
			await ignoreTrackerSignal({
				signalId: signal.id,
				data: {
					action: "IGNORE",
					feedback,
					listPersonId: context?.listPersonId,
					prospectId: context?.prospectId,
					trackerOrganizationId: context?.trackerOrganizationId ?? undefined,
				},
			}).unwrap();
			dispatch(
				removeTrackerSignalResultFromInbox({
					listPersonId: context?.listPersonId,
					trackerSignalResultId: signal.id,
				})
			);
			setIsIgnoreModalOpen(false);
		} catch {
			setSubmitError(t("profileTracker.signalActions.ignoreFeedbackError"));
		}
	};

	return (
		<div
			className="mt-3 flex flex-row flex-wrap gap-2 border-t border-border-light pt-3"
			onClick={stopPropagation}
			onKeyDown={stopPropagation}
		>
			<div className="w-fit">
				<Button
					color={ButtonColor.DANGER_SECONDARY}
					size={ButtonSize.XS}
					title={t("profileTracker.signalActions.ignore")}
					type="button"
					onClick={() => setIsIgnoreModalOpen(true)}
				/>
			</div>
			<div className="w-fit">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					disabled={!onReachOut}
					size={ButtonSize.XS}
					title={t("profileTracker.signalActions.reachOutTo", { name: prospectName })}
					type="button"
					onClick={onReachOut}
				/>
			</div>
			{isCopywritingActive && onGenerateDrafts && (
				<div className="w-fit">
					<Button
						color={ButtonColor.ACTION}
						disabled={isGeneratingDrafts || prospectNotResolved}
						isLoading={isGeneratingDrafts}
						size={ButtonSize.XS}
						title={t("profileTracker.signalActions.generateDrafts")}
						type="button"
						onClick={onGenerateDrafts}
					/>
				</div>
			)}
			<SignalIgnoreFeedbackModal
				isOpened={isIgnoreModalOpen}
				isSubmitting={isLoading}
				submitError={submitError}
				onClose={() => setIsIgnoreModalOpen(false)}
				onSubmit={handleIgnoreSubmit}
			/>
		</div>
	);
};
